import { BoxV2 as Box, Button, Flex, TextInput } from 'portal-commons';
import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { faSquareMinus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useControllers } from './hooks';
import { LiftSuspensionFormValues } from './types';
import { CampaignDetail } from '@/apis/core';
import Modal from '@/components/ModalV2';

const DefaultFormValues: LiftSuspensionFormValues = {
  explanation: '',
};

interface LiftSuspensionModalProps {
  open?: boolean;
  disabled?: boolean;
  campaign: CampaignDetail;
  onClose?: () => void;
  onSubmit?: (explanation: string) => void;
}

const LiftSuspensionModal: FC<LiftSuspensionModalProps> = ({
  open,
  disabled,
  campaign,
  onClose,
  onSubmit,
}) => {
  const {
    control,
    formState: { errors },
    reset,
    handleSubmit,
  } = useForm<LiftSuspensionFormValues>({
    defaultValues: DefaultFormValues,
  });
  const { explanation, setExplanation, validateExplanation, explanationError } =
    useControllers(control);

  const handleCancel = () => {
    onClose?.();
  };

  const handleValid = (value: LiftSuspensionFormValues) => {
    onSubmit?.(value.explanation);
  };

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open]);

  return (
    <Modal
      data-testid="liftSuspensionModal"
      open={open}
      sx={{ padding: '24px 32px' }}
    >
      <form
        style={{ width: '100%' }}
        noValidate
        onSubmit={handleSubmit(handleValid)}
      >
        <Flex
          sx={{
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'xl',
            color: 't.black70',
            fontWeight: 400,
            lineHeight: 'normal',
          }}
        >
          <Flex
            sx={{
              alignItems: 'center',
              gap: 's',
            }}
          >
            <Flex
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px',
                color: 't.black80',
                fontSize: '22px',
              }}
            >
              <FontAwesomeIcon icon={faSquareMinus} />
            </Flex>
            <Box sx={{ color: 't.black90', fontWeight: 600, fontSize: 'H600' }}>
              Lift Suspension
            </Box>
          </Flex>

          <Flex sx={{ flexDirection: 'column', gap: 'm', width: '100%' }}>
            <Box sx={{ fontSize: 'H100' }}>* Indicates a Required Field</Box>
            <TextInput
              data-testid="liftSuspensionModalCampaignUid"
              disabled
              label="Campaign ID"
              value={campaign.uid}
            />
            <TextInput
              data-testid="liftSuspensionModalBrandName"
              disabled
              label="Brand DBA Name"
              value={campaign.brandName}
            />
            <TextInput
              data-testid="liftSuspensionModalExplanation"
              required
              disabled={disabled}
              error={!!explanationError?.message}
              label="Explanation"
              note={explanationError?.message}
              value={explanation}
              onChange={setExplanation}
              onBlur={validateExplanation}
            />
          </Flex>

          <Flex sx={{ gap: '28px' }}>
            <Button
              data-testid="liftSuspensionModalCancelButton"
              type="button"
              variant="outline"
              disabled={disabled}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              data-testid="liftSuspensionModalSubmitButton"
              type="submit"
              disabled={
                disabled ||
                Object.values(errors).some((value) => !!value.message)
              }
            >
              Lift Suspension
            </Button>
          </Flex>
        </Flex>
      </form>
    </Modal>
  );
};

export default LiftSuspensionModal;
